<template>
  <div class="manufacturer-product-lines">
    <div v-if="!data && loading" class="text-center my-6"><v-progress-circular indeterminate color="primary" /></div>

    <v-card v-else-if="data" outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        {{ $t('product-line.title') }}
        <v-spacer />
        <v-text-field v-model="search" :label="$t('product-line.search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable style="max-width: 260px" class="mr-2" />
        <v-btn v-if="canEdit" small outlined @click="creating = {name: '', saving: false}"><v-icon small left>mdi-plus</v-icon>{{ $t('product-line.create') }}</v-btn>
      </v-card-title>
      <v-card-subtitle>{{ $t('product-line.hint') }}</v-card-subtitle>

      <!-- Gammes qui commencent pareil : un clic les coche pour les réunir -->
      <v-card-text v-if="canEdit && data.suggestions.length" class="pt-0">
        <div class="text-caption text--secondary mb-1">{{ $t('product-line.suggestions') }}</div>
        <div class="d-flex flex-wrap" style="gap: 8px">
          <v-chip v-for="suggestion in data.suggestions" :key="suggestion.prefix" small outlined @click="select(suggestion.productLineIds)">
            <span class="mono">{{ suggestion.prefix }}</span>&nbsp;· {{ $tc('product-line.lines-count', suggestion.productLineIds.length, {count: suggestion.productLineIds.length}) }}
          </v-chip>
        </div>
      </v-card-text>

      <!-- Réunir les gammes cochées, sous un nom -->
      <v-card-text v-if="canEdit && selected.length" class="merge-bar">
        <div class="d-flex align-center flex-wrap" style="gap: 12px">
          <span class="text-body-2">{{ $tc('product-line.selected', selected.length, {count: selected.length}) }}</span>
          <v-text-field v-model="mergeName" :label="$t('product-line.merged-name')" outlined dense hide-details style="flex: 1 1 260px" />
          <v-btn small text @click="selected = []">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="selected.length < 2 || !mergeName.trim()" :loading="merging" @click="merge">{{ $t('product-line.merge') }}</v-btn>
        </div>
      </v-card-text>

      <!-- Une gamme créée à la main, vide : on y range ensuite des types (menu ⋮ de chaque type) -->
      <v-card-text v-if="creating" class="merge-bar">
        <div class="d-flex align-center flex-wrap" style="gap: 12px">
          <v-text-field v-model="creating.name" :label="$t('product-line.new-name')" outlined dense hide-details autofocus style="flex: 1 1 260px" @keyup.enter="create" />
          <v-btn small text @click="creating = null">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!creating.name.trim()" :loading="creating.saving" @click="create">{{ $t('save') }}</v-btn>
        </div>
      </v-card-text>

      <div v-for="line in shownLines" :key="line.id" class="line">
        <v-divider />
        <div class="d-flex align-center flex-wrap px-4 pt-3" style="gap: 8px">
          <v-checkbox v-if="canEdit" v-model="selected" :value="line.id" dense hide-details class="mt-0 pt-0" />
          <template v-if="renaming && renaming.id === line.id">
            <v-text-field v-model="renaming.name" outlined dense hide-details autofocus style="max-width: 320px" @keyup.enter="rename" />
            <v-btn small text @click="renaming = null">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" :loading="renaming.saving" @click="rename">{{ $t('save') }}</v-btn>
          </template>
          <template v-else>
            <div class="font-weight-medium">{{ line.name }}</div>
            <v-btn v-if="canEdit" icon x-small :aria-label="$t('product-line.rename')" @click="renaming = {id: line.id, name: line.name, saving: false}"><v-icon x-small>mdi-pencil</v-icon></v-btn>
          </template>
          <v-spacer />
          <span class="text-caption text--secondary">{{ $tc('product-line.types-count', line.types.length, {count: line.types.length}) }} · {{ $tc('product-line.charging-points', line.chargingPoints, {count: line.chargingPoints}) }}</span>
          <v-btn v-if="line.chargingPoints" x-small text color="primary" :to="{name: 'charging-points', query: {manufacturerId, productLineId: line.id}}">{{ $t('product-line.see-charging-points') }}</v-btn>
        </div>
        <v-simple-table dense class="px-2 pb-2">
          <tbody>
            <tr v-for="type in line.types" :key="type.id">
              <td class="mono text-no-wrap" style="width: 30%">
                <router-link :to="{name: 'charging-point-types.detail', params: {id: type.id}}">{{ type.model || '-' }}</router-link>
              </td>
              <td class="text-caption">{{ type.modelLabel && type.modelLabel !== type.model ? type.modelLabel : '' }}<span v-if="type.maxPower" class="text--secondary"> · {{ type.maxPower }} kW</span></td>
              <td class="text-caption text-right text-no-wrap">{{ $tc('product-line.charging-points', type.chargingPoints, {count: type.chargingPoints}) }}</td>
              <td class="text-right text-no-wrap" style="width: 1%">
                <v-menu v-if="canEdit" left offset-y max-height="360">
                  <template #activator="{on, attrs}">
                    <v-btn icon x-small v-bind="attrs" v-on="on" :aria-label="$t('product-line.place')"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="line.types.length > 1" @click="place(type, null)">
                      <v-list-item-title>{{ $t('product-line.own-line') }}</v-list-item-title>
                    </v-list-item>
                    <v-subheader v-if="data.lines.length > 1">{{ $t('product-line.move-to') }}</v-subheader>
                    <v-list-item v-for="other in data.lines.filter(l => l.id !== line.id)" :key="other.id" @click="place(type, other.id)">
                      <v-list-item-title>{{ other.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
      <v-card-text v-if="!data.lines.length" class="text--secondary">{{ $t('product-line.none') }}</v-card-text>
      <v-card-text v-else-if="!shownLines.length" class="text--secondary">{{ $t('product-line.none-found') }}</v-card-text>
    </v-card>
  </div>
</template>

<script>
import ProductLineRepository from "@repository/ProductLineRepository";
import ManufacturerRepository from "@repository/ManufacturerRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

/**
 * Les gammes et les types d'un constructeur, premier onglet de sa fiche : Constructeur › Gamme › Type. Chaque type est toujours dans une gamme,
 * par défaut celle de son modèle ; on réunit ici celles qui sont la même borne, on les renomme, on déplace un type.
 */
export default {
  mixins: [FirmwareMixin],

  props: {
    manufacturerId: {type: String, required: true},
  },

  data() {
    return {
      data: null,
      loading: false,
      selected: [],
      mergeName: '',
      merging: false,
      renaming: null,
      creating: null,
      search: '',
    }
  },

  computed: {
    canEdit() {
      return this.canEditManufacturer
    },
    // Par nom de gamme ou par modèle de l'un de ses types.
    shownLines() {
      const search = (this.search || '').toLowerCase()
      return this.data.lines.filter(line => !search || line.name.toLowerCase().includes(search)
          || line.types.some(type => [type.model, type.modelLabel].some(value => (value || '').toLowerCase().includes(search))))
    },
  },

  mounted() {
    this.load()
  },

  watch: {
    manufacturerId() {
      this.data = null
      this.selected = []
      this.load()
    },
    // Le nom proposé : celui de la gamme cochée la plus répandue.
    selected(ids) {
      const lines = (this.data?.lines || []).filter(line => ids.includes(line.id))
      lines.sort((a, b) => b.chargingPoints - a.chargingPoints)
      this.mergeName = lines.length ? lines[0].name : ''
    },
  },

  methods: {
    load() {
      this.loading = true
      return ProductLineRepository.ofManufacturer(this.manufacturerId).then(data => {
        this.data = data
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    select(ids) {
      this.selected = [...ids]
    },

    create() {
      this.creating.saving = true
      ManufacturerRepository.createProductLine(this.manufacturerId, this.creating.name.trim()).then(() => {
        this.creating = null
        return this.load()
      }).catch(err => {
        this.notifyError(err)
        this.creating.saving = false
      })
    },

    // La gamme cochée la plus répandue garde son identifiant (et les firmwares qui la visent) : elle passe en premier.
    merge() {
      this.$dialog.confirm({text: this.$tc('product-line.merge-confirm', this.selected.length, {count: this.selected.length, name: this.mergeName.trim()})}).then(result => {
        if (result) {
          this.doMerge()
        }
      })
    },

    doMerge() {
      const ids = [...this.selected].sort((a, b) => this.count(b) - this.count(a))
      this.merging = true
      ProductLineRepository.merge(ids, this.mergeName.trim()).then(() => {
        this.selected = []
        this.notifySuccess('product-line.merged')
        return this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.merging = false
      })
    },

    rename() {
      this.renaming.saving = true
      ProductLineRepository.rename(this.renaming.id, this.renaming.name.trim()).then(() => {
        this.renaming = null
        return this.load()
      }).catch(err => {
        this.notifyError(err)
        this.renaming.saving = false
      })
    },

    place(type, productLineId) {
      const target = productLineId ? this.data.lines.find(line => line.id === productLineId) : null
      this.$dialog.confirm({text: target ? this.$t('product-line.move-confirm', {model: type.model || type.modelLabel, name: target.name}) : this.$t('product-line.own-line-confirm', {model: type.model || type.modelLabel})}).then(result => {
        if (!result) {
          return
        }
        ProductLineRepository.place(type.id, productLineId).then(() => {
          this.notifySuccess('product-line.moved')
          return this.load()
        }).catch(err => {
          this.notifyError(err)
        })
      })
    },

    count(id) {
      return (this.data.lines.find(line => line.id === id) || {chargingPoints: 0}).chargingPoints
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.merge-bar {
  background: rgba(0, 0, 0, 0.03);
}
</style>
